import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Check, HeartHandshake, Lock, MessagesSquare, Scale, ShieldCheck, Sparkles } from 'lucide-react';
import {
  ButtonLink,
  CardGrid,
  Container,
  CTABanner,
  FeatureCard,
  FeatureRow,
  Lead,
  PageHero,
  Section,
  SectionHeader,
  Steps,
} from '../components/page';
import { Avatar, Glow } from '../components/ui';

/* ------------------------------------------------------------------ */
/* Mock mediation case                                                 */
/* ------------------------------------------------------------------ */

const TIMELINE = [
  { title: 'Case opened', detail: 'Kate S. requested help for Group Trip Fund' },
  { title: 'Mediator joined', detail: 'Neutral, confidential support' },
  { title: 'Both sides heard', detail: 'Kate S. and Mike R. shared their view' },
  { title: 'Resolution agreed', detail: 'Contribution plan accepted by the group', done: true },
];

const CaseVisual = () => (
  <div className="relative">
    <Glow className="-inset-8 bg-violet-500/15" />
    <div className="surface relative !bg-ink-900/90 p-6 sm:p-8">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/[0.06] text-white">
            <MessagesSquare className="h-5 w-5" />
          </span>
          <div>
            <p className="text-sm font-semibold text-white">Resolution Center</p>
            <p className="text-xs text-slate-500">Group Trip Fund · Hawaii 2025</p>
          </div>
        </div>
        <span className="flex items-center gap-1.5 rounded-full border border-violet-400/20 bg-violet-400/10 px-3 py-1 text-xs font-medium text-violet-300">
          <Lock className="h-3 w-3" />
          Private
        </span>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <div className="flex -space-x-2">
          <Avatar initials="KS" color="green" size="sm" />
          <Avatar initials="MR" color="amber" size="sm" />
        </div>
        <span className="text-xs text-slate-500">+ Groupmoney mediator</span>
      </div>

      <ol className="relative mt-8 space-y-5 border-l border-white/10 pl-6">
        {TIMELINE.map((step, i) => (
          <motion.li
            key={step.title}
            initial={{ opacity: 0, x: -10 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25 + i * 0.25, duration: 0.5 }}
            className="relative"
          >
            <span
              className={clsx(
                'absolute -left-[33px] top-0.5 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-ink-900',
                step.done ? 'bg-emerald-400 text-ink-950' : 'bg-brand-400/20 text-brand-300'
              )}
            >
              <Check className="h-3 w-3" strokeWidth={4} />
            </span>
            <p className={clsx('text-sm font-semibold', step.done ? 'text-emerald-300' : 'text-white')}>{step.title}</p>
            <p className="mt-0.5 text-sm text-slate-400">{step.detail}</p>
          </motion.li>
        ))}
      </ol>

      <div className="mt-8 rounded-2xl border border-brand-400/25 bg-brand-400/[0.07] p-4">
        <p className="flex items-center gap-2 text-sm font-medium text-white">
          <Sparkles className="h-4 w-4 text-brand-300" />
          Groupmoney Mediator
        </p>
        <p className="mt-1.5 text-sm text-slate-300">Glad we found a plan that works for everyone. Your group is all set.</p>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Copy                                                                */
/* ------------------------------------------------------------------ */

const APPROACH = [
  {
    icon: Scale,
    title: 'Mediating with Care',
    text: 'We work neutrally to facilitate discussions and find agreeable solutions between members.',
  },
  {
    icon: ShieldCheck,
    accent: 'violet',
    title: 'Protecting Privacy',
    text: 'Everything you share with us stays confidential—we respect your trust and keep all details private.',
  },
  {
    icon: HeartHandshake,
    accent: 'pink',
    title: 'Preserving Relationships',
    text: 'Resolving issues amicably, where connections are maintained, is at the heart of what we do.',
  },
];

const HOW = [
  { title: 'Log In', text: 'Access your Groupmoney account.' },
  { title: 'Go to Profile', text: 'Click on your profile icon in the top-right corner.' },
  { title: 'Find Resolution Center', text: 'Look for the "Resolution Center" option and select it.' },
  { title: 'Choose Your Group', text: "Pick the group needing assistance, and we'll immediately engage with you." },
];

const PLANS = [
  { plan: 'Free', features: 'Self-help guides, 1 chat support per month' },
  { plan: 'Basic', features: 'Full live support, mediation services' },
  { plan: 'Max', features: 'Priority support, advanced mediation, personalized guidance' },
];

const PlanTable = () => (
  <div className="surface-inset overflow-hidden !rounded-2xl">
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="border-b border-white/[0.06] text-xs uppercase tracking-[0.14em] text-slate-500">
          <th scope="col" className="px-5 py-3.5 font-semibold">Plan</th>
          <th scope="col" className="px-5 py-3.5 font-semibold">Features</th>
        </tr>
      </thead>
      <tbody>
        {PLANS.map(({ plan, features }) => (
          <tr key={plan} className="border-b border-white/[0.04] last:border-0">
            <th scope="row" className="whitespace-nowrap px-5 py-4 font-semibold text-white">{plan}</th>
            <td className="px-5 py-4 text-slate-400">{features}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

const ResolutionCenterPage = () => (
  <>
    <PageHero
      highlight="Resolution Center"
      title="We're Here to Support Your Group"
      description="Resolving conflicts while preserving what matters most—your relationships."
    />

    <Section className="!pt-8 sm:!pt-12">
      <Container>
        <SectionHeader title="Putting Relationships First" />
        <Lead className="mt-8">
          At Groupmoney, we know that conflicts within a group can be tough—whether it's about contributions, spending
          decisions, or member disagreements. We understand how challenging these moments can be, and that's why our top
          priority is always <span className="font-semibold text-white">people first</span>. Our goal is to help you
          resolve issues in a way that preserves relationships and keeps your group strong.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Our Approach"
          title="Our Approach to Conflict Resolution"
          description={`We're not here to take sides or judge. Our "Resolution Center" is dedicated to:`}
        />
        <CardGrid cols={3} className="mt-16 sm:mt-20">
          {APPROACH.map(({ icon, accent, title, text }) => (
            <FeatureCard key={title} icon={icon} accent={accent} title={title}>
              {text}
            </FeatureCard>
          ))}
        </CardGrid>
        <Lead className="mt-16">
          Whether it's a misunderstanding over funds or a clash of opinions, we're committed to guiding your group toward
          a fair and peaceful resolution.
        </Lead>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader
          eyebrow="Getting Support"
          title="How to Get Help"
          description="Getting support is quick and easy. Here's how to start:"
        />
        <Steps steps={HOW} className="mt-16 sm:mt-20" />

        <div className="mt-24 sm:mt-32">
          <FeatureRow icon={MessagesSquare} accent="violet" eyebrow="Support Plans" title="Available Support by Plan" visual={<CaseVisual />}>
            <p>
              Once you initiate, our team will connect with you—offering guidance, mediation, or resources tailored to your
              situation. Free users get basic support (guides, 1 chat/month), while Basic and Max users enjoy full live
              help and mediation.
            </p>
            <PlanTable />
          </FeatureRow>
        </div>
      </Container>
    </Section>

    <Section>
      <Container>
        <SectionHeader eyebrow="Our Promise" title="Our Promise to You" />
        <Lead className="mt-8">
          We prioritize your group's harmony above all. Resolving conflicts in an amicable manner, where relationships are
          preserved, is our mission. With the "Resolution Center," you're not alone—we're here to listen, mediate, and
          support you every step of the way. Let's keep your group thriving together!
        </Lead>
      </Container>
    </Section>

    <CTABanner
      title="Need Help Now?"
      description="Log in and start the process today."
      note={
        <>
          For questions, email{' '}
          <a href="mailto:support@groupmoney.app" className="font-medium text-brand-300 hover:text-white">
            support@groupmoney.app
          </a>
        </>
      }
    >
      <ButtonLink to="/#get-started">Get Started</ButtonLink>
      <ButtonLink to="/contact" variant="secondary">
        Contact Support
      </ButtonLink>
      <ButtonLink to="/contact#faq" variant="secondary">
        View FAQs
      </ButtonLink>
    </CTABanner>
  </>
);

export default ResolutionCenterPage;
